<template>
  <div class="bar-chart-container">
    <div ref="chartRef" class="chart-view" />
  </div>
</template>

<script setup>
import * as echarts from 'echarts'
import { ref, onMounted, onUnmounted, watch } from 'vue'

// Props
const props = defineProps({
  data: {
    type: Object,
    default: () => ({ categories: [], loginData: [], orderData: [] })
  },
  title: {
    type: String,
    default: '登陆次数和下单次数的总计对比'
  }
})

const chartRef = ref(null)
let chartInstance = null

const initChart = () => {
  if (!chartRef.value) return
  
  chartInstance = echarts.init(chartRef.value)
  
  // 检测是否为移动端
  const isMobile = window.innerWidth <= 768
  
  const option = {
    title: { 
      text: props.title,
      top: isMobile ? '5px' : '10px',
      left: 'center',
      textStyle: {
        fontSize: isMobile ? 13 : 16,
        fontWeight: 'bold',
        color: '#303133'
      }
    },
    tooltip: { 
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      confine: true // 限制在图表区域内
    },
    legend: { 
      data: ['登录次数', '下单次数'],
      top: isMobile ? '25px' : '45px',
      left: 'center',
      itemWidth: isMobile ? 20 : 25,
      itemHeight: isMobile ? 12 : 14,
      textStyle: {
        fontSize: isMobile ? 11 : 12
      }
    },
    grid: {
      left: isMobile ? '8%' : '3%',
      right: isMobile ? '5%' : '4%',
      bottom: isMobile ? '8%' : '3%',
      top: isMobile ? '65px' : '85px',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: props.data.categories,
      axisTick: {
        alignWithLabel: true
      },
      axisLabel: {
        fontSize: isMobile ? 8 : 12,
        rotate: isMobile ? 30 : 0, // 移动端倾斜显示
        interval: 0
      }
    },
    yAxis: { 
      type: 'value',
      name: '次数',
      nameTextStyle: {
        fontSize: isMobile ? 11 : 12
      },
      nameGap: isMobile ? 8 : 15,
      axisLabel: {
        fontSize: isMobile ? 10 : 12
      }
    },
    series: [
      {
        name: '登录次数',
        type: 'bar',
        data: props.data.loginData,
        itemStyle: {
          color: '#409EFF'
        },
        barWidth: isMobile ? '30%' : '35%'
      },
      {
        name: '下单次数',
        type: 'bar',
        data: props.data.orderData,
        itemStyle: {
          color: '#67C23A'
        },
        barWidth: isMobile ? '30%' : '35%'
      }
    ]
  }
  
  chartInstance.setOption(option)
}

const resizeChart = () => {
  if (chartInstance) {
    chartInstance.resize()
  }
}

// 监听数据变化
watch(() => props.data, () => {
  if (chartInstance) {
    chartInstance.setOption({
      xAxis: {
        data: props.data.categories
      },
      series: [
        {
          data: props.data.loginData
        },
        {
          data: props.data.orderData
        }
      ]
    })
  }
}, { deep: true })

onMounted(() => {
  setTimeout(initChart, 100)
  window.addEventListener('resize', resizeChart)
})

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
  window.removeEventListener('resize', resizeChart)
})

// 暴露方法给父组件
defineExpose({
  resizeChart
})
</script>

<style scoped>
.bar-chart-container {
  width: 100%;
  height: 100%;
}

.chart-view {
  width: 100%;
  height: 400px;
}

/* 移动端优化 */
@media screen and (max-width: 768px) {
  .chart-view {
    height: 350px;
  }
}
</style>
